import {
  buildTrack,
  getTrackDef,
  Track,
  RaceMode,
  RoomStateMsg,
  RaceStartMsg,
  ResultEntry,
  ServerMsg,
  BotDifficulty,
  ticksToTimeString,
  TICK_RATE,
  dailySeed,
  GhostData,
} from '@voidline/shared';
import {
  Settings,
  Progression,
  loadSettings,
  saveSettings,
  loadProgression,
  saveProgression,
  loadGhost,
  saveGhost,
  levelForXp,
  MedalTier,
} from './persist.js';
import { InputManager } from './input.js';
import { AudioEngine } from './audio/audio.js';
import { GameRenderer } from './render/renderer.js';
import { Hud } from './ui/hud.js';
import { Connection } from './net/connection.js';
import { LocalGame, LocalGameConfig } from './game/localGame.js';
import { NetGame } from './game/netGame.js';
import { GameDriver, GameEvents, makeShipView, RaceView } from './game/view.js';
import { makeFrame } from '@voidline/shared';
import { Vector3 as ThreeVector3 } from 'three';
import { toast, clear, MenuNav } from './ui/dom.js';
import { TouchControls, isTouchCapable } from './ui/touch.js';
import { buildTitleScreen } from './ui/screens/title.js';
import { buildMenuScreen } from './ui/screens/menu.js';
import { buildSoloScreen } from './ui/screens/solo.js';
import { buildGarageScreen } from './ui/screens/garage.js';
import { buildSettingsScreen } from './ui/screens/settings.js';
import { buildMultiplayerScreen } from './ui/screens/multiplayer.js';
import { buildLobbyScreen, updateLobbyScreen } from './ui/screens/lobby.js';
import { buildResultsScreen } from './ui/screens/results.js';
import { buildPauseOverlay } from './ui/screens/pause.js';
import { buildChampionshipScreen } from './ui/screens/championship.js';
import { buildStatsScreen } from './ui/screens/stats.js';
import { buildTutorialOverlay, TutorialController } from './ui/screens/tutorial.js';
import { PerfOverlay } from './ui/perf.js';

export type ScreenName =
  | 'title'
  | 'menu'
  | 'solo'
  | 'garage'
  | 'settings'
  | 'multiplayer'
  | 'lobby'
  | 'championship'
  | 'stats'
  | 'race'
  | 'results';

export interface SoloRaceRequest {
  trackId: string;
  trackSeed: string | null;
  mode: RaceMode;
  laps: number;
  botCount: number;
  botDifficulty: BotDifficulty;
  /** For championship sequencing. */
  tag?: string;
  useGhost?: boolean;
  tutorial?: boolean;
}

interface RaceContext {
  online: boolean;
  trackId: string;
  trackSeed: string | null;
  mode: RaceMode;
  fieldSize: number;
  tag?: string;
  tutorial?: boolean;
}

export class App {
  settings: Settings;
  progression: Progression;
  input: InputManager;
  audio: AudioEngine;
  renderer: GameRenderer;
  hud: Hud;
  perf: PerfOverlay;
  nav = new MenuNav();

  conn: Connection | null = null;
  driver: GameDriver | null = null;
  raceCtx: RaceContext | null = null;
  roomState: RoomStateMsg | null = null;
  lastResults: ResultEntry[] | null = null;
  lastRaceStart: RaceStartMsg | null = null;
  tutorial: TutorialController | null = null;
  /** Room-browser screens subscribe here for roomList responses. */
  onRoomList: ((rooms: import('@voidline/shared').RoomListing[]) => void) | null = null;

  screenRoot: HTMLElement;
  screen: ScreenName = 'title';
  private paused = false;
  private pauseOverlay: HTMLElement | null = null;
  private touchControls: TouchControls | null = null;
  private lastFrame = performance.now();
  private fpsCounter = { frames: 0, t: 0 };
  private backdropView: RaceView | null = null;
  private resultsDelay = 0;
  private spectateIndex = 0;
  private disposed = false;

  constructor(private uiRoot: HTMLElement, canvas: HTMLCanvasElement) {
    this.settings = loadSettings();
    this.progression = loadProgression();
    this.input = new InputManager(this.settings);
    this.audio = new AudioEngine(this.settings);
    this.renderer = new GameRenderer(canvas, this.settings);
    this.screenRoot = document.createElement('div');
    this.screenRoot.style.position = 'absolute';
    this.screenRoot.style.inset = '0';
    uiRoot.appendChild(this.screenRoot);
    this.hud = new Hud(uiRoot, this.settings);
    this.hud.setVisible(false);
    this.perf = new PerfOverlay(uiRoot);

    // on-screen controls for touch devices (phones, tablets, touch laptops)
    if (isTouchCapable()) {
      this.touchControls = new TouchControls(uiRoot, () => {
        if (this.screen === 'race' && !this.paused) this.setPaused(true);
      });
      this.input.touch = this.touchControls;
    }

    document.body.classList.toggle('reduced-motion', this.settings.reducedMotion);

    window.addEventListener('resize', () => this.renderer.resize());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.audio.suspend();
        if (this.screen === 'race' && !this.raceCtx?.online) this.setPaused(true);
      } else {
        this.audio.resume();
      }
    });
    // unlock audio on first gesture
    const unlock = () => {
      this.audio.unlock();
      this.audio.startMusic(7, 118);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    // gamepad menu nav
    this.input.onMenuNav = (dir) => {
      if (this.screen === 'race') return;
      if (dir === 'up') this.nav.move(-1);
      else if (dir === 'down') this.nav.move(1);
      else if (dir === 'confirm') this.nav.confirm();
      this.audio.play('ui_move');
    };
    // keyboard menu nav (arrows/enter) whenever we're not racing
    window.addEventListener('keydown', (e) => {
      if (this.screen === 'race' && !this.pauseOverlay) return;
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.code === 'ArrowUp') {
        this.nav.move(-1);
        this.audio.play('ui_move');
        e.preventDefault();
      } else if (e.code === 'ArrowDown') {
        this.nav.move(1);
        this.audio.play('ui_move');
        e.preventDefault();
      } else if (e.code === 'Enter') {
        this.nav.confirm();
      }
    });
    this.input.onGamepadChange = (name) => {
      toast(name ? `Gamepad connected: ${name.slice(0, 40)}` : 'Gamepad disconnected', !name);
    };

    this.show('title');
    requestAnimationFrame((t) => this.loop(t));
  }

  saveAll(): void {
    saveSettings(this.settings);
    saveProgression(this.progression);
  }

  playerLevel(): number {
    return levelForXp(this.progression.xp);
  }

  // ── screens ───────────────────────────────────────────────────────────────

  show(name: ScreenName): void {
    this.screen = name;
    clear(this.screenRoot);
    this.nav.clearFocus();
    this.hud.setVisible(name === 'race');
    this.touchControls?.setVisible(name === 'race');

    let el: HTMLElement | null = null;
    switch (name) {
      case 'title':
        el = buildTitleScreen(this);
        break;
      case 'menu':
        el = buildMenuScreen(this);
        break;
      case 'solo':
        el = buildSoloScreen(this);
        break;
      case 'garage':
        el = buildGarageScreen(this);
        break;
      case 'settings':
        el = buildSettingsScreen(this);
        break;
      case 'multiplayer':
        el = buildMultiplayerScreen(this);
        break;
      case 'lobby':
        el = buildLobbyScreen(this);
        break;
      case 'championship':
        el = buildChampionshipScreen(this);
        break;
      case 'stats':
        el = buildStatsScreen(this);
        break;
      case 'results':
        el = buildResultsScreen(this);
        break;
      case 'race':
        el = null;
        break;
    }
    if (el) this.screenRoot.appendChild(el);
    this.nav.refresh(this.screenRoot);

    if (name !== 'race' && name !== 'results') this.ensureBackdrop();
  }

  /** Menu background: a lit track scene so the game sells itself from the title screen. */
  ensureBackdrop(): void {
    if (this.driver) return; // an active race renders instead
    if (this.backdropView) return;
    try {
      const track = buildTrack('ionport');
      const view: RaceView = {
        track,
        mode: 'race',
        laps: 3,
        phase: 'countdown',
        countdownSeconds: 99,
        raceTicks: 0,
        ships: [
          makeShipView({
            shipIndex: 0,
            playerId: null,
            name: 'SHOW',
            vehicleId: this.progression.selectedVehicle,
            paintId: this.progression.selectedPaints[this.progression.selectedVehicle] ?? 'academy',
            isBot: true,
          }),
        ],
        standings: [0],
        localIndex: -1,
        local: null,
        ghost: null,
        net: null,
        eliminationIn: null,
      };
      // pose the showcase ship on the start line
      const frame = makeFrame();
      track.main.frameAt(6, frame);
      view.ships[0].pos.copy(frame.pos).addScaled(frame.up, 1.4);
      view.ships[0].quat.setFromBasis(frame.right, frame.up, frame.tangent);
      this.backdropView = view;
      this.renderer.loadRace(view);
      this.renderer.setCameraMode('podium');
    } catch (err) {
      console.error('backdrop failed', err);
    }
  }

  clearBackdrop(): void {
    if (this.backdropView) {
      this.renderer.unloadRace();
      this.backdropView = null;
    }
  }

  // ── solo races ────────────────────────────────────────────────────────────

  startSoloRace(req: SoloRaceRequest): void {
    this.clearBackdrop();
    this.driver?.dispose();
    this.driver = null;

    let track: Track;
    try {
      track = buildTrack(req.trackId, req.trackSeed);
    } catch (err) {
      console.error(err);
      toast('Track failed to build — falling back to Ionport', true);
      track = buildTrack('ionport');
    }

    const ghost =
      req.useGhost !== false && (req.mode === 'timetrial' || req.trackSeed)
        ? loadGhost(req.trackId, req.trackSeed)
        : null;

    const cfg: LocalGameConfig = {
      track,
      trackId: req.trackId,
      trackSeed: req.trackSeed,
      mode: req.mode,
      laps: req.laps,
      playerVehicle: this.progression.selectedVehicle,
      playerPaint:
        this.progression.selectedPaints[this.progression.selectedVehicle] ?? 'academy',
      playerName: this.settings.playerName || 'YOU',
      botCount: req.botCount,
      botDifficulty: req.botDifficulty,
      ghost,
      events: this.gameEvents(false),
    };
    const game = new LocalGame(cfg, this.input, this.settings);
    this.driver = game;
    this.raceCtx = {
      online: false,
      trackId: req.trackId,
      trackSeed: req.trackSeed,
      mode: req.mode,
      fieldSize: 1 + (req.mode === 'timetrial' ? 0 : req.botCount),
      tag: req.tag,
      tutorial: req.tutorial,
    };
    this.lastResults = null;
    this.resultsDelay = 0;
    this.renderer.loadRace(game.view);
    this.renderer.setCameraMode('chase');
    this.hud.prepareTrack(game.view);
    this.setPaused(false);
    this.show('race');
    this.audio.stopMusic();
    this.audio.startMusic(trackMusicSeed(req.trackId, req.trackSeed), 128 + track.def.difficulty * 6);

    if (req.tutorial) {
      this.tutorial = buildTutorialOverlay(this, game);
    }
  }

  // ── multiplayer ───────────────────────────────────────────────────────────

  ensureConnection(onReady: () => void): void {
    if (this.conn?.connected) {
      onReady();
      return;
    }
    this.conn?.close();
    const conn = new Connection(this.settings.serverUrl, this.settings.playerName || 'PILOT');
    this.conn = conn;
    let readyFired = false;
    conn.onMessage = (msg) => {
      if (msg.type === 'welcome' && !readyFired) {
        readyFired = true;
        onReady();
      }
      this.onServerMsg(msg);
    };
    conn.onStateChange = (state) => {
      if (state === 'failed') {
        toast('Could not reach the VOIDLINE server.', true);
        if (this.screen === 'lobby' || this.screen === 'race') {
          this.abandonOnlineRace('Connection lost.');
        }
      } else if (state === 'reconnecting') {
        toast('Connection lost — reconnecting…', true);
      }
    };
    conn.connect();
  }

  private onServerMsg(msg: ServerMsg): void {
    switch (msg.type) {
      case 'roomState': {
        this.roomState = msg;
        if (this.screen === 'lobby') updateLobbyScreen(this);
        // phase transitions
        if (msg.phase === 'lobby' && (this.screen === 'race' || this.screen === 'results')) {
          this.leaveRaceView();
          this.show('lobby');
        } else if (this.screen !== 'lobby' && this.screen !== 'race' && this.screen !== 'results' && msg.phase === 'lobby') {
          this.show('lobby');
        }
        break;
      }
      case 'raceStart': {
        this.lastRaceStart = msg;
        this.startNetRace(msg);
        break;
      }
      case 'raceEnd': {
        this.lastResults = msg.results;
        if (this.driver instanceof NetGame) this.driver.markOver();
        this.awardOnlineResults(msg.results);
        setTimeout(() => {
          if (this.screen === 'race') this.showResults();
        }, 2200);
        break;
      }
      case 'playerFinished': {
        const ship = this.driver?.view.ships[msg.shipIndex];
        if (ship && this.screen === 'race') {
          this.hud.showMessage(`${ship.info.name} FINISHED P${msg.placement}`, 2);
        }
        break;
      }
      case 'eliminated': {
        const ship = this.driver?.view.ships[msg.shipIndex];
        const local = msg.shipIndex === this.driver?.view.localIndex;
        if (ship) {
          this.hud.showMessage(local ? 'ELIMINATED' : `${ship.info.name} ELIMINATED`, 2.4);
          this.audio.play('eliminated');
        }
        break;
      }
      case 'chat': {
        if (this.screen === 'lobby') updateLobbyScreen(this, { chat: { from: msg.from, text: msg.text } });
        else if (this.screen === 'race') this.hud.showMessage(`${msg.from}: ${msg.text}`, 3);
        break;
      }
      case 'roomList': {
        this.onRoomList?.(msg.rooms);
        break;
      }
      case 'error': {
        toast(msg.message, true);
        break;
      }
      case 'kicked': {
        toast(`Removed from server: ${msg.reason}`, true);
        this.abandonOnlineRace('');
        break;
      }
    }
  }

  private startNetRace(msg: RaceStartMsg): void {
    if (!this.conn) return;
    this.clearBackdrop();
    this.driver?.dispose();

    let track: Track;
    try {
      track = buildTrack(msg.trackId, msg.trackSeed);
    } catch (err) {
      console.error(err);
      toast('Failed to build the race track!', true);
      return;
    }
    const game = new NetGame(msg, track, this.conn, this.input, this.settings, this.gameEvents(true));
    this.driver = game;
    this.raceCtx = {
      online: true,
      trackId: msg.trackId,
      trackSeed: msg.trackSeed,
      mode: msg.mode,
      fieldSize: msg.ships.length,
    };
    this.lastResults = null;
    this.spectateIndex = 0;
    this.renderer.loadRace(game.view);
    this.renderer.setCameraMode(msg.yourShipIndex >= 0 ? 'chase' : 'orbit');
    this.hud.prepareTrack(game.view);
    this.setPaused(false);
    this.show('race');
    this.audio.stopMusic();
    this.audio.startMusic(trackMusicSeed(msg.trackId, msg.trackSeed), 128 + track.def.difficulty * 6);
    if (msg.yourShipIndex < 0) toast('Race in progress — spectating until the next round.');
  }

  quickplay(): void {
    this.ensureConnection(() => {
      this.conn!.send({
        type: 'quickplay',
        vehicleId: this.progression.selectedVehicle,
        paintId: this.progression.selectedPaints[this.progression.selectedVehicle] ?? 'academy',
      });
    });
  }

  createRoom(isPrivate: boolean): void {
    this.ensureConnection(() => {
      this.conn!.send({
        type: 'createRoom',
        isPrivate,
        vehicleId: this.progression.selectedVehicle,
        paintId: this.progression.selectedPaints[this.progression.selectedVehicle] ?? 'academy',
      });
    });
  }

  joinRoom(code: string): void {
    this.ensureConnection(() => {
      this.conn!.send({
        type: 'joinRoom',
        code,
        vehicleId: this.progression.selectedVehicle,
        paintId: this.progression.selectedPaints[this.progression.selectedVehicle] ?? 'academy',
      });
    });
  }

  leaveRoom(): void {
    this.conn?.send({ type: 'leaveRoom' });
    this.roomState = null;
    this.leaveRaceView();
    this.show('multiplayer');
  }

  abandonOnlineRace(message: string): void {
    if (message) toast(message, true);
    this.roomState = null;
    this.leaveRaceView();
    if (this.screen === 'race' || this.screen === 'lobby' || this.screen === 'results') {
      this.show('multiplayer');
    }
  }

  private leaveRaceView(): void {
    this.driver?.dispose();
    this.driver = null;
    this.raceCtx = null;
    this.tutorial?.dispose();
    this.tutorial = null;
    this.renderer.unloadRace();
    this.audio.engineOff();
    this.setPaused(false);
    this.audio.stopMusic();
    this.audio.startMusic(7, 118);
  }

  // ── race flow ─────────────────────────────────────────────────────────────

  private gameEvents(online: boolean): GameEvents {
    return {
      onCountdownBeep: (n) => {
        this.audio.play('count_beep');
        this.hud.showCountdown(String(n));
      },
      onGo: () => {
        this.audio.play('count_go');
        this.hud.showCountdown('GO');
      },
      onCheckpoint: (local) => {
        if (local) this.audio.play('checkpoint');
      },
      onLapComplete: (local, lapTicks, best) => {
        if (!local) return;
        this.audio.play(best ? 'best_lap' : 'lap');
        this.hud.showMessage(
          `${best ? 'BEST ' : ''}LAP ${ticksToTimeString(lapTicks)}`,
          1.8
        );
      },
      onFinish: (local, placement) => {
        if (local) {
          this.audio.play('finish');
          this.hud.showMessage(placementText(placement), 3);
          if (!online) {
            this.awardSoloFinish();
          }
        }
      },
      onEliminated: (_i, local) => {
        if (local) {
          this.audio.play('eliminated');
        }
      },
      onWallHit: (intensity, local) => {
        if (local) {
          this.audio.play('wall_hit', Math.min(1, intensity / 25));
          this.renderer.addShake(Math.min(0.7, intensity / 40));
        }
      },
      onShipHit: (intensity, local) => {
        if (local) {
          this.audio.play('ship_hit', Math.min(1, intensity / 20));
          this.renderer.addShake(Math.min(0.9, intensity / 26));
        }
      },
      onBoostPad: (local) => {
        if (local) {
          this.audio.play('boost_pad');
          this.renderer.addShake(0.12);
        }
      },
      onLand: (intensity, local) => {
        if (local) {
          this.audio.play('land', Math.min(1, intensity / 16));
          this.renderer.addShake(Math.min(0.5, intensity / 30));
        }
      },
      onRespawn: (local) => {
        if (local) this.audio.play('respawn');
      },
      onFallOff: (local) => {
        if (local) this.renderer.addShake(0.5);
      },
      onRaceEnd: () => {
        // local games: brief cinematic pause, then results
        this.resultsDelay = 2.4;
      },
    };
  }

  showResults(): void {
    if (this.driver instanceof LocalGame) {
      this.lastResults = this.driver.results();
    }
    this.renderer.setCameraMode('podium');
    this.show('results');
  }

  /** Rematch / continue actions from the results screen. */
  finishRace(action: 'menu' | 'lobby' | 'rematch'): void {
    const ctx = this.raceCtx;
    if (ctx?.online) {
      if (action === 'lobby' || action === 'rematch') {
        this.conn?.send({ type: 'returnToLobby' });
        this.conn?.send({ type: 'setReady', ready: action === 'rematch' });
        this.leaveRaceView();
        this.show('lobby');
      } else {
        this.leaveRoom();
        this.show('menu');
      }
      return;
    }
    const req = ctx
      ? {
          trackId: ctx.trackId,
          trackSeed: ctx.trackSeed,
          mode: ctx.mode,
          laps: 3,
          botCount: Math.max(0, ctx.fieldSize - 1),
          botDifficulty: 'standard' as BotDifficulty,
        }
      : null;
    this.leaveRaceView();
    if (action === 'rematch' && req) this.startSoloRace(req);
    else this.show('menu');
  }

  // ── progression ───────────────────────────────────────────────────────────

  private awardSoloFinish(): void {
    const ctx = this.raceCtx;
    const game = this.driver;
    if (!ctx || !(game instanceof LocalGame)) return;
    const view = game.view;
    const local = view.local;
    if (!local || !local.finished) return;

    const p = this.progression;
    const placement = local.placement;
    p.races++;
    if (placement === 1 && ctx.mode !== 'timetrial') p.wins++;
    if (placement <= 3 && ctx.mode !== 'timetrial') p.podiums++;

    let xp = 40 + Math.max(0, ctx.fieldSize - placement) * 22;
    if (placement === 1 && ctx.fieldSize > 1) xp += 45;
    if (ctx.mode === 'timetrial') xp = 30;

    // records + medals
    const key = ctx.trackSeed ? `seed:${ctx.trackSeed}` : ctx.trackId;
    const rec = p.records[key] ?? { bestLapTicks: 0, bestRaceTicks: 0, medal: null, races: 0, wins: 0 };
    rec.races++;
    if (placement === 1 && ctx.mode !== 'timetrial') rec.wins++;
    if (local.bestLapTicks > 0 && (rec.bestLapTicks === 0 || local.bestLapTicks < rec.bestLapTicks)) {
      rec.bestLapTicks = local.bestLapTicks;
    }
    if (local.finishTick > 0 && (rec.bestRaceTicks === 0 || local.finishTick < rec.bestRaceTicks)) {
      rec.bestRaceTicks = local.finishTick;
    }
    // medal from best lap vs track medal times
    const def = getTrackDef(ctx.trackId) ?? view.track.def;
    if (def?.medals && rec.bestLapTicks > 0) {
      const lapSec = rec.bestLapTicks / TICK_RATE;
      const tier: MedalTier | null =
        lapSec <= def.medals.author
          ? 'author'
          : lapSec <= def.medals.gold
            ? 'gold'
            : lapSec <= def.medals.silver
              ? 'silver'
              : lapSec <= def.medals.bronze
                ? 'bronze'
                : null;
      const order: (MedalTier | null)[] = [null, 'bronze', 'silver', 'gold', 'author'];
      if (order.indexOf(tier) > order.indexOf(rec.medal)) {
        rec.medal = tier;
        if (tier) {
          toast(`${tier.toUpperCase()} medal — ${def.name}!`);
          xp += { bronze: 20, silver: 40, gold: 70, author: 120 }[tier];
        }
      }
    }
    p.records[key] = rec;

    // vehicle stats
    const vs = p.vehicleStats[p.selectedVehicle] ?? { races: 0, wins: 0, distanceKm: 0 };
    vs.races++;
    if (placement === 1 && ctx.mode !== 'timetrial') vs.wins++;
    p.vehicleStats[p.selectedVehicle] = vs;

    // daily challenge record
    if (ctx.trackSeed === dailySeed().replace('voidline-daily-', '') || ctx.trackSeed === dailySeed()) {
      const prev = p.dailyBest[ctx.trackSeed] ?? 0;
      if (local.finishTick > 0 && (prev === 0 || local.finishTick < prev)) {
        p.dailyBest[ctx.trackSeed] = local.finishTick;
        toast('New daily challenge best!');
      }
    }

    // championship scoring
    if (ctx.tag?.startsWith('champ:')) {
      import('./ui/screens/championship.js').then(({ recordChampionshipResult }) => {
        const msg = recordChampionshipResult(this, ctx.tag!, placement);
        if (msg) toast(msg, false, 5000);
      });
    }

    const before = levelForXp(p.xp);
    p.xp += xp;
    const after = levelForXp(p.xp);
    if (after > before) {
      toast(`LEVEL UP — Level ${after}`);
      this.audio.play('best_lap');
    }

    // ghost save on time-trial / seed PB
    const ghost = game.buildRecordedGhost();
    if (ghost && (ctx.mode === 'timetrial' || ctx.trackSeed)) {
      const existing = loadGhost(ctx.trackId, ctx.trackSeed);
      if (!existing || ghost.totalTicks < existing.totalTicks) {
        saveGhost(ghost);
        toast('Ghost saved — race yourself next time.');
      }
    }

    this.saveAll();
  }

  private awardOnlineResults(results: ResultEntry[]): void {
    const view = this.driver?.view;
    if (!view || view.localIndex < 0) return;
    const mine = results.find((r) => r.shipIndex === view.localIndex);
    if (!mine || mine.dnf) return;
    const p = this.progression;
    p.races++;
    if (mine.placement === 1) p.wins++;
    if (mine.placement <= 3) p.podiums++;
    const humans = results.length;
    let xp = 60 + Math.max(0, humans - mine.placement) * 30;
    if (mine.placement === 1) xp += 70;
    const before = levelForXp(p.xp);
    p.xp += xp;
    if (levelForXp(p.xp) > before) toast(`LEVEL UP — Level ${levelForXp(p.xp)}`);
    this.saveAll();
  }

  // ── pause ─────────────────────────────────────────────────────────────────

  setPaused(paused: boolean): void {
    this.paused = paused;
    this.driver?.setPaused?.(paused && !this.raceCtx?.online);
    if (paused && !this.pauseOverlay) {
      this.pauseOverlay = buildPauseOverlay(this);
      this.uiRoot.appendChild(this.pauseOverlay);
      this.nav.refresh(this.pauseOverlay);
    } else if (!paused && this.pauseOverlay) {
      this.pauseOverlay.remove();
      this.pauseOverlay = null;
      if (this.screen !== 'race') this.nav.refresh(this.screenRoot);
    }
  }

  quitRace(): void {
    const online = this.raceCtx?.online;
    this.setPaused(false);
    this.leaveRaceView();
    if (online) {
      this.conn?.send({ type: 'leaveRoom' });
      this.roomState = null;
      this.show('multiplayer');
    } else {
      this.show('menu');
    }
  }

  // ── main loop ─────────────────────────────────────────────────────────────

  private loop(t: number): void {
    if (this.disposed) return;
    requestAnimationFrame((tt) => this.loop(tt));
    let dt = (t - this.lastFrame) / 1000;
    this.lastFrame = t;
    if (dt > 0.25) dt = 0.25;
    if (dt <= 0) return;

    // fps
    this.fpsCounter.frames++;
    this.fpsCounter.t += dt;
    if (this.fpsCounter.t >= 0.5) {
      this.renderer.setFps(Math.round(this.fpsCounter.frames / this.fpsCounter.t));
      this.fpsCounter.frames = 0;
      this.fpsCounter.t = 0;
    }

    // pause toggle
    if (this.input.consumePause()) {
      if (this.screen === 'race') {
        this.setPaused(!this.paused);
        this.audio.play(this.paused ? 'ui_confirm' : 'ui_back');
      } else if (this.pauseOverlay) {
        this.setPaused(false);
      }
    }

    // sample pad nav even in menus (sample() also polls capture + nav)
    if (this.screen !== 'race') {
      this.input.sample(dt, menuScratchInput);
    }

    const driver = this.driver;
    if (driver && this.screen === 'race') {
      driver.update(dt);
      const view = driver.view;

      // engine audio from local (or spectated) ship
      const ship =
        view.localIndex >= 0 ? view.ships[view.localIndex] : view.ships[view.standings[this.spectateIndex] ?? 0];
      if (ship) {
        this.audio.updateEngine(
          Math.min(1, ship.speed / 105),
          ship.boosting ? 1 : Math.min(1, ship.speed / 60),
          ship.boosting,
          ship.grounded
        );
        // positional audio for nearby rivals (optional AudioEngine capability)
        const au = this.audio as unknown as {
          updateNearbyShips?: (
            ships: { x: number; y: number; z: number; speed: number; boosting: boolean }[],
            listener: {
              x: number; y: number; z: number;
              fwdX: number; fwdY: number; fwdZ: number;
              upX: number; upY: number; upZ: number;
            }
          ) => void;
        };
        if (au.updateNearbyShips && view.localIndex >= 0) {
          const me = view.ships[view.localIndex];
          const others = view.ships
            .filter((s, i) => i !== view.localIndex && s.visible && !s.eliminated)
            .map((s) => ({ x: s.pos.x, y: s.pos.y, z: s.pos.z, speed: s.speed, boosting: s.boosting }));
          const cam = this.renderer.chase.camera;
          const f = new ThreeVector3(0, 0, -1).applyQuaternion(cam.quaternion);
          au.updateNearbyShips(others, {
            x: me.pos.x, y: me.pos.y, z: me.pos.z,
            fwdX: f.x, fwdY: f.y, fwdZ: f.z,
            upX: 0, upY: 1, upZ: 0,
          });
        }
      }

      this.renderer.renderRace(view, dt, this.input.lookBack, this.spectateIndex);
      this.hud.update(view, dt);
      this.tutorial?.update(dt);

      // spectator target cycling
      if (view.localIndex < 0 && this.input.lookBack) {
        // lookBack cycles spectate target (edge handled by cooldown)
        if (!this.spectateCooldown) {
          this.spectateIndex = (this.spectateIndex + 1) % Math.max(1, view.ships.length);
          this.spectateCooldown = 0.4;
        }
      }
      if (this.spectateCooldown) this.spectateCooldown = Math.max(0, this.spectateCooldown - dt);

      // offline results transition
      if (this.resultsDelay > 0) {
        this.resultsDelay -= dt;
        if (this.resultsDelay <= 0 && driver.isOver() && !this.raceCtx?.online) {
          this.showResults();
        }
      }
    } else if (driver && this.screen === 'results') {
      // keep the world alive behind the results panel (podium orbit)
      driver.update(dt);
      this.renderer.renderRace(driver.view, dt, false, 0);
      this.audio.engineOff();
    } else if (this.backdropView) {
      this.renderer.renderRace(this.backdropView, dt * 0.4, false, 0);
    }

    this.perf.update(this.renderer.stats, this.driver?.view.net ?? null);
  }

  private spectateCooldown = 0;
}

const menuScratchInput = {
  steer: 0,
  pitch: 0,
  throttle: 0,
  brakeL: 0,
  brakeR: 0,
  boost: false,
  respawn: false,
};

function placementText(p: number): string {
  const suffix = p === 1 ? 'st' : p === 2 ? 'nd' : p === 3 ? 'rd' : 'th';
  return p === 1 ? 'VICTORY' : `${p}${suffix} PLACE`;
}

function trackMusicSeed(trackId: string, seed: string | null): number {
  let h = 0;
  const s = seed ?? trackId;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return (h % 1000) + 1;
}
